import BrandLogo from '../components/BrandLogo';
/**
 * 🌌 UniversalPage — Página pública /universal
 *
 * Muestra los cálculos numerológicos UNIVERSALES (compartidos para todos,
 * sin necesidad de iniciar sesión):
 *   - Saludo "Hola, universo!" + binomios universales (Año/Mes/Semana/Día).
 *   - Tarjeta "Energía del Día Universal".
 *   - 4 mensajes universales (Trabajo, Amor, Bienestar, Espiritual)
 *     generados por OpenAI y cacheados de forma compartida.
 *   - Calendario navegable del año actual (12 meses) mostrando
 *     únicamente el día universal en cada celda.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Briefcase, Heart, Sparkles } from "lucide-react";
import { generateBatchUniversalMessages } from "../services/api";
import {
  IMPORTANT_DAYS,
  getImportantDayColor,
  getImportantDayInfo,
  isImportantDay
} from "../utils/importantDays";

// ═════════════════════════════════════════════════════════════════════════
// Utilidades de numerología (reducción respetando 11 y 22) — versión pura
// ═════════════════════════════════════════════════════════════════════════
const sumDigits = (n) =>
  Math.abs(n)
    .toString()
    .split("")
    .reduce((a, b) => a + Number(b), 0);

const reduceNum = (n) => {
  let x = Math.abs(n);
  while (x >= 10 && x !== 11 && x !== 22) x = sumDigits(x);
  return x;
};

const universalYear = (Y) => reduceNum(Y);
const universalMonth = (M, Y) => reduceNum(universalYear(Y) + M);
const universalDay = (D, M, Y) => reduceNum(universalYear(Y) + M + D);

// Semana Universal: fórmula encadenada por número de semana del mes (1-4).
const weekIndex = (date) => {
  const day = date.getDate();
  if (day <= 7) return 1;
  if (day <= 14) return 2;
  if (day <= 21) return 3;
  return 4;
};

const universalWeek = (date) => {
  const M = date.getMonth() + 1;
  const Y = date.getFullYear();
  const week = weekIndex(date);

  const reducedMonth = reduceNum(M);
  const reducedYear = reduceNum(Y);

  const weekOne = reduceNum(reducedMonth + reducedYear);
  if (week === 1) return weekOne;

  const weekTwo = reduceNum(reducedYear + weekOne);
  if (week === 2) return weekTwo;

  const weekThree = reduceNum(weekOne + weekTwo);
  if (week === 3) return weekThree;

  return reduceNum(reducedMonth + weekOne);
};

// ═════════════════════════════════════════════════════════════════════════
// Diccionarios de descripciones cortas por número (mismos que la app
// privada para mantener coherencia visual)
// ═════════════════════════════════════════════════════════════════════════
const NUM_KEYWORDS = {
  1: { k: "liderazgo e independencia", caution: "postergar o ceder tu decisión" },
  2: { k: "colaboración y escucha", caution: "hacerlo en soledad" },
  3: { k: "expresión y creatividad", caution: "guardarte tus ideas" },
  4: { k: "orden y ejecución", caution: "improvisar" },
  5: { k: "cambio y exploración", caution: "apegarte a un plan" },
  6: { k: "cuidado y responsabilidad", caution: "cargar de más" },
  7: { k: "profundidad y estudio", caution: "dispersarte" },
  8: { k: "poder y logros", caution: "controlar en exceso" },
  9: { k: "cierre y proyección pública", caution: "dramatizar" },
  11: { k: "inspiración y visión", caution: "dudar de tu intuición" },
  22: { k: "progreso y construcción mayor", caution: "subestimarte" }
};

// ═════════════════════════════════════════════════════════════════════════
// Construcción de matriz mensual del calendario (igual lógica que AppPage)
// ═════════════════════════════════════════════════════════════════════════
function buildMonthMatrix(baseDate) {
  const Y = baseDate.getFullYear();
  const M = baseDate.getMonth();
  const first = new Date(Y, M, 1);
  let startDow = first.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const daysInMonth = new Date(Y, M + 1, 0).getDate();
  const prevMonthDays = new Date(Y, M, 0).getDate();
  const cells = [];

  for (let i = 0; i < startDow; i++) {
    const day = prevMonthDays - startDow + 1 + i;
    cells.push({ date: new Date(Y, M - 1, day), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(Y, M, d), inMonth: true });
  }
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, inMonth: next.getMonth() === M });
  }
  return cells;
}

// ═════════════════════════════════════════════════════════════════════════
// Subcomponentes visuales (estilo equivalente al de la página privada)
// ═════════════════════════════════════════════════════════════════════════
const UniversalHeader = () => (
  <nav className="relative z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-3 sm:min-h-16">
        <div className="flex items-center gap-2 md:gap-3">
          <BrandLogo className="w-40 sm:w-56" />
        </div>
        <a
          href="https://tienda.numerologia-cotidiana.com/#cat-planes-numerathum"
          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-morado px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-morado"
        >
          <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
          Consigue tu acceso personal a Numerathum
        </a>
      </div>
    </div>
  </nav>
);

const UniversalChip = ({ label, value, desc }) => (
  <div className="aspect-square p-2 md:p-3 rounded-lg md:rounded-xl bg-morado/10 border border-morado/20 flex flex-col items-center justify-center text-center">
    <div className="w-11 h-11 md:w-14 md:h-14 rounded-full flex items-center justify-center font-bold text-morado bg-white border-2 border-[#d4af37] mb-2 md:mb-3 text-sm md:text-lg leading-tight">
      {value}
    </div>
    <div className="space-y-0.5 md:space-y-1">
      <div className="text-xs uppercase tracking-wide text-morado font-medium leading-tight">
        {label}
      </div>
      <div className="text-xs text-gray-700 leading-tight">{desc}</div>
    </div>
  </div>
);

const EnergiaDelDiaUniversalCard = ({ ud }) => {
  const info = NUM_KEYWORDS[ud] || NUM_KEYWORDS[1];
  const isMaster = ud === 11 || ud === 22;
  const emoji = ud === 11 ? "💡11" : ud === 22 ? "🚀22" : "✨";

  const text = isMaster
    ? `Día Maestro ${ud}: ${info.k}. Es un día de vibración elevada que invita a todos a aprovechar esta energía colectiva.`
    : `Hoy la energía universal vibra en ${ud}: ${info.k}. Es un día ideal para enfocarse en estas cualidades a nivel colectivo.`;

  const explanation = `Aprovecha la energía universal del número ${ud} para alinearte con el flujo del día. Recuerda: evita ${info.caution}.`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border shadow-lg overflow-hidden"
      style={{ borderColor: "#d4af37" }}
    >
      <div
        className="px-4 py-3 text-white"
        style={{
          background:
            "var(--color-morado)"
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">{emoji}</span>
            <div>
              <h4 className="font-semibold">Energía del Día</h4>
              <p className="text-xs opacity-90">
                Vibración universal compartida por todos hoy
              </p>
            </div>
          </div>
          <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold bg-white/20 border-2 border-white/40 text-base">
            {ud}
          </div>
        </div>
      </div>
      <div className="p-4 bg-white text-gray-800">
        <p className="leading-relaxed font-medium mb-2">{text}</p>
        <p className="text-sm text-gray-600 leading-relaxed">{explanation}</p>
      </div>
    </motion.div>
  );
};

const AreaCard = ({ title, icon: Icon, gradient, message, isLoading }) => {
  // Separar el "⚠️ Evita:" si el modelo lo incluyó como última línea.
  const evitaMatch = message?.match(/⚠️\s*Evita:\s*([\s\S]*)$/);
  const evitaText = evitaMatch ? evitaMatch[1].trim() : null;
  const mainText = evitaMatch
    ? message.replace(evitaMatch[0], "").trim()
    : message;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl shadow-xl border overflow-hidden"
      style={{ borderColor: "#d4af37" }}
    >
      <div className="p-4 md:p-5 text-white" style={{ background: gradient }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-white/15">
            <Icon size={22} />
          </div>
          <h4 className="text-lg md:text-xl font-semibold">{title}</h4>
        </div>
      </div>
      <div className="p-4 md:p-5 bg-white relative min-h-[120px]">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-3"
            >
              <div
                className="w-12 h-12 rounded-full border-4 border-[#d4af37] border-t-morado"
                style={{
                  background:
                    "var(--color-morado)",
                  opacity: 0.2
                }}
              />
              <p className="text-sm text-morado font-medium">
                Generando mensaje universal...
              </p>
            </motion.div>
          </div>
        ) : (
          <>
            <p className="text-gray-800 leading-relaxed whitespace-pre-line">
              {mainText}
            </p>
            {evitaText && (
              <p className="mt-3 text-sm text-gray-600">
                <span className="font-medium text-morado">⚠️ Evita:</span>{" "}
                {evitaText}
              </p>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

const UniversalDaySticker = ({ ud }) => {
  if (!isImportantDay(ud)) return null;
  const info = getImportantDayInfo(ud);
  if (!info) return null;
  return (
    <div
      title={info.label}
      className="absolute inset-0 flex items-center justify-center text-sm sm:text-base md:text-lg lg:text-xl select-none pointer-events-none"
    >
      {info.emoji}
    </div>
  );
};

const UniversalCalendarMonth = ({
  currentDate,
  onPrevMonth,
  onNextMonth,
  canPrevMonth,
  canNextMonth
}) => {
  const matrix = useMemo(() => buildMonthMatrix(currentDate), [currentDate]);

  return (
    <div
      className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border"
      style={{ borderColor: "#d4af37" }}
    >
      <div className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onPrevMonth}
          disabled={!canPrevMonth}
          className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
            canPrevMonth
              ? "text-morado hover:bg-morado/10 cursor-pointer"
              : "text-gray-300 cursor-not-allowed"
          }`}
          title={canPrevMonth ? "Mes anterior" : "Inicio del año"}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="hidden lg:inline text-sm font-medium">
            Mes anterior
          </span>
        </button>

        <div className="flex-1 text-center">
          <div className="text-morado font-semibold text-sm md:text-base capitalize">
            {currentDate.toLocaleDateString("es-ES", {
              month: "long",
              year: "numeric"
            })}
          </div>
          <div className="text-xs text-gray-600 hidden sm:block mt-1">
            Leyenda: 🌟3 • 💰8 • 💡11 • 🚀22
          </div>
        </div>

        <button
          type="button"
          onClick={onNextMonth}
          disabled={!canNextMonth}
          className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
            canNextMonth
              ? "text-morado hover:bg-morado/10 cursor-pointer"
              : "text-gray-300 cursor-not-allowed"
          }`}
          title={canNextMonth ? "Mes siguiente" : "Fin del año"}
        >
          <span className="hidden lg:inline text-sm font-medium">
            Mes siguiente
          </span>
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      <div className="px-1 sm:px-2 md:px-3 pb-3">
        <div className="grid grid-cols-7 text-center text-xs font-medium text-morado mb-1.5">
          {["L", "M", "M", "J", "V", "S", "D"].map((d, idx) => (
            <div key={`${d}-${idx}`} className="py-1.5 px-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 md:gap-2">
          {matrix.map((cell, idx) => {
            const d = cell.date;
            const ud = universalDay(
              d.getDate(),
              d.getMonth() + 1,
              d.getFullYear()
            );
            const inMonth = cell.inMonth;

            const today = new Date();
            const isToday =
              d.getDate() === today.getDate() &&
              d.getMonth() === today.getMonth() &&
              d.getFullYear() === today.getFullYear();

            const baseClass = inMonth ? "bg-white" : "bg-gray-50 opacity-60";
            const importantClass = isImportantDay(ud)
              ? getImportantDayColor(ud)
              : "border-gray-200";

            const stateClass = isToday
              ? "border-4 border-morado shadow-lg shadow-morado/20 bg-morado/5 ring-2 ring-morado/30 ring-offset-2"
              : `border ${importantClass} ${
                  isImportantDay(ud)
                    ? getImportantDayColor(ud)
                    : baseClass
                }`;

            const hasIcon = isImportantDay(ud);

            return (
              <div
                key={idx}
                className={`
                  relative rounded-lg md:rounded-xl p-1.5 sm:p-2
                  min-h-[3.5rem] sm:min-h-[4rem] md:min-h-[4.5rem] lg:min-h-[5rem]
                  text-left ${stateClass}
                `}
              >
                {isToday && (
                  <div className="absolute top-0 right-0 bg-morado text-white text-[0.5rem] sm:text-[0.6rem] font-bold px-1 sm:px-1.5 py-0.5 rounded-bl-lg rounded-tr-lg">
                    HOY
                  </div>
                )}

                {hasIcon && <UniversalDaySticker ud={ud} />}

                <div className="absolute top-1 left-1">
                  <span
                    className={`text-xs sm:text-sm font-medium ${
                      isToday ? "text-morado font-bold" : "text-gray-500"
                    }`}
                  >
                    {d.getDate()}
                  </span>
                </div>

                {hasIcon ? (
                  <div className="absolute bottom-1 right-1">
                    <div className="text-[0.55rem] sm:text-[0.65rem] md:text-xs font-bold text-morado bg-white/90 rounded-full px-1.5 py-0.5 border border-[#d4af37]/60">
                      {ud}
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span
                      className={`text-base sm:text-lg md:text-xl font-bold ${
                        isToday ? "text-morado" : "text-morado/80"
                      }`}
                    >
                      {ud}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════
// Página principal
// ═════════════════════════════════════════════════════════════════════════
const CATEGORIES = ["work", "love", "wellness", "spiritual"];

export default function UniversalPage() {
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();

  const nums = useMemo(() => {
    const D = today.getDate();
    const M = today.getMonth() + 1;
    const Y = today.getFullYear();
    return {
      UY: universalYear(Y),
      UM: universalMonth(M, Y),
      UW: universalWeek(today),
      UD: universalDay(D, M, Y)
    };
  }, [today]);

  // Mes visible en el calendario (navegable dentro del año actual)
  const [calendarMonth, setCalendarMonth] = useState(
    () => new Date(currentYear, today.getMonth(), 1)
  );

  const canPrevMonth = calendarMonth.getMonth() > 0;
  const canNextMonth = calendarMonth.getMonth() < 11;

  const handlePrevMonth = useCallback(() => {
    setCalendarMonth((prev) => {
      if (prev.getMonth() === 0) return prev;
      return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setCalendarMonth((prev) => {
      if (prev.getMonth() === 11) return prev;
      return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
    });
  }, []);

  // Carga de mensajes universales del backend
  const [messages, setMessages] = useState({
    work: "",
    love: "",
    wellness: "",
    spiritual: ""
  });
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadMessages = async () => {
      setIsLoadingMessages(true);
      setLoadError(null);
      try {
        const result = await generateBatchUniversalMessages(
          CATEGORIES,
          nums.UD
        );
        if (cancelled) return;
        setMessages(result);
      } catch (error) {
        if (cancelled) return;
        console.error("Error cargando mensajes universales:", error);
        setLoadError(
          error?.message ||
            "No fue posible cargar los mensajes universales. Intenta recargar la página."
        );
      } finally {
        if (!cancelled) setIsLoadingMessages(false);
      }
    };

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [nums.UD]);

  const formattedToday = today.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });

  return (
    <div className="min-h-screen bg-morado/5">
      <UniversalHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 space-y-6">
        {/* Saludo + energía universal del día */}
        <section
          className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-4 md:p-6 border"
          style={{ borderColor: "#d4af37" }}
        >
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-morado mb-1">
                  Hola, universo! 👋
                </h1>
                <div className="w-16 h-0.5 bg-morado rounded-full" />
              </div>
              <div className="text-right">
                <div className="text-lg md:text-xl font-bold text-morado capitalize">
                  {formattedToday}
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              Energía universal del día — visible para cualquier visitante, sin
              datos personales.
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-morado font-extrabold bg-white border-2 shadow-sm text-lg md:text-2xl"
                  style={{ borderColor: "#d4af37" }}
                >
                  {nums.UD}
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wide text-morado font-semibold">
                    ENERGÍA DEL DÍA
                  </div>
                  <div className="text-sm text-gray-700">
                    {NUM_KEYWORDS[nums.UD]?.k || "clima del día"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Binomios universales (Año / Mes / Semana / Día) */}
        <section>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
            <UniversalChip
              label="Año Universal"
              value={nums.UY}
              desc="telón colectivo"
            />
            <UniversalChip
              label="Mes Universal"
              value={nums.UM}
              desc="vibración del mes"
            />
            <UniversalChip
              label="Semana Universal"
              value={nums.UW}
              desc={NUM_KEYWORDS[nums.UW]?.k || "energía universal"}
            />
            <UniversalChip
              label="Día Universal"
              value={nums.UD}
              desc="clima del día"
            />
          </div>
        </section>

        {/* Energía del día universal + 4 áreas */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <EnergiaDelDiaUniversalCard ud={nums.UD} />
          </div>

          {loadError && (
            <div className="md:col-span-2 rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
              {loadError}
            </div>
          )}

          <AreaCard
            title="Trabajo / Proyecto"
            icon={Briefcase}
            gradient="var(--color-morado)"
            message={messages.work}
            isLoading={isLoadingMessages}
          />
          <AreaCard
            title="Amor / Vínculos"
            icon={Heart}
            gradient="var(--color-morado)"
            message={messages.love}
            isLoading={isLoadingMessages}
          />
          <AreaCard
            title="Bienestar"
            icon={Activity}
            gradient="var(--color-morado)"
            message={messages.wellness}
            isLoading={isLoadingMessages}
          />
          <AreaCard
            title="Espiritual"
            icon={Sparkles}
            gradient="var(--color-morado)"
            message={messages.spiritual}
            isLoading={isLoadingMessages}
          />
        </section>

        {/* Explicación breve del calendario */}
        <section className="bg-morado/5 border border-morado/20 rounded-2xl p-4 md:p-5 text-sm text-gray-700 leading-relaxed">
          <div className="font-semibold text-morado mb-1">
            ¿Cómo leer el calendario?
          </div>
          Cada día muestra <strong>un número</strong> que representa la{" "}
          <strong>energía universal</strong> de ese día. Los días con colores de
          fondo y emojis son <strong>días especiales</strong> con energías
          potenciadas que puedes aprovechar.
        </section>

        {/* Calendario universal del año actual */}
        <section>
          <UniversalCalendarMonth
            currentDate={calendarMonth}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            canPrevMonth={canPrevMonth}
            canNextMonth={canNextMonth}
          />

          {/* Leyenda de días importantes */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(IMPORTANT_DAYS).map(([num, info]) => (
              <div
                key={num}
                className={`rounded-xl p-3 border ${getImportantDayColor(
                  Number(num)
                )}`}
              >
                <div className="flex items-center gap-2 font-semibold text-morado">
                  <span className="text-lg">{info.emoji}</span>
                  <span>
                    Día {num} · {info.label}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1 leading-snug">
                  {info.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="py-8 text-center text-xs text-gray-600">
        Hecho con 💜 y ✨ — Numerathum: Mi Brújula del Tiempo
      </footer>
    </div>
  );
}
